import { deleteBuilding, updateBuilding } from "@/api/project/building";
import { createModel } from "@/api/project/model";
import { Building } from "@/commons/interface/project/building";
import { Model } from "@/commons/interface/project/model";
import TnInput from "@/commons/widget/input/tn-input";
import TnMessage from "@/commons/widget/message";
import classNames from "classnames";
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { ContextMenuFC } from "tncet-common";
import { ReactComponent as ShowSvg } from './icons/show-building.svg';
import { ReactComponent as AddSvg } from './icons/add.svg';
import { ReactComponent as ConfirmSvg } from './icons/confirm.svg';
import { Button, Form, Input, Modal, Select, message } from "antd";
import { MODEL_TYPE_LIST, ModelType } from "@/commons/enums/model-file-type";
import TnModal from "@/commons/widget/base-modal";
import ModelItem from "./model-item";
import genUUID from "@/commons/utils/gen-uuid";
import ConfirmModal from "@/commons/widget/monix-modal";

interface IProps {
    building: Building // 楼栋
    onDeleteBuilding: () => void // 删除了楼栋
    onUpdateBuilding: (data: Building) => void // 更新了楼栋
    // togglePreview: (modelUuid) => void // 显示/隐藏模型
    previewModelUuids: string[]
}

export default function BuildingItem(props: IProps) {
    const { building } = props;
    const [item, setItem] = useState<Building>();
    const [editing, setEditing] = useState<boolean>(false); // 是否处于编辑状态
    const [newModel, setNewModel] = useState<Model>(); // 当前正在添加的模型
    const [cmOpen, setCmOpen] = useState<boolean>(false); // 是否展开建筑的右键菜单
    const [top, setTop] = useState<number>(); // 右键菜单top
    const [left, setLeft] = useState<number>(); // 右键菜单left
    const [adding, setAdding] = useState<boolean>(false); // 当前正在添加模型状态

    const [modalOpen, setModalOpen] = useState<boolean>(false); // 控制绑定楼栋号弹窗的显示
    const [loading, setLoading] = useState<boolean>(false); // 控制提交按钮加载状态
    const [form] = Form.useForm();

    useEffect(() => {
        setItem(building);
    }, [building])

    // 点击了展开收起
    const handleExpandItem = () => {
        if (editing) return;
        setItem({ ...item, expanded: !item.expanded });
    }

    // 点击了添加模型
    const onClickAddModel = () => {
        // 楼栋没展开时，展开
        if (!item.expanded) {
            setItem({ ...item, expanded: true });
        }
        if (newModel != null) {
            // 当前正在添加，直接返回
            return;
        }
        // 新添加一行模型
        setNewModel({});
    }

    // 添加模型
    const onAddModel = () => {
        if (adding) return;
        setAdding(true);
        newModel.uuid = genUUID();
        newModel.buildingUuid = item.uuid;
        newModel.projectUuid = item.projectUuid;
        createModel(newModel).then(res => {
            let modelId: number = res.data;
            if (item.modelList == null) {
                item.modelList = [];
            }
            item.modelList.push({ ...newModel, id: modelId });
            setItem({ ...item });
            setNewModel(null);
        }).catch(err => {
            TnMessage.error("添加模型失败")
        }).finally(() => {
            setNewModel(null);
            setAdding(false);
        })
    }

    // 右键楼栋
    const onRightClickBuilding = (e) => {
        setCmOpen(true);
        setLeft(e.clientX);
        setTop(e.clientY);
    }

    // 更新楼栋
    const onUpdateBuilding = (uuid: string, data) => {
        updateBuilding(uuid, data).then(res => {
            setItem({ ...item, ...data });
        })
    }

    // 点击绑定楼栋号
    const onClickLink = () => {
        form.setFieldsValue({ buildingNumber: item.buildingNumber }); // 预填充当前楼栋号
        setModalOpen(true);
    }

    // 更新保存楼栋号的回调函数
    const onConfirmSave = () => {
        form.validateFields().then(values => {
            const updatedBuildingNumber = values.buildingNumber;
            if (!item) return;

            // 更新数据库中的楼栋号
            updateBuilding(item.uuid, { buildingNumber: updatedBuildingNumber }).then(res => {
                if (res.status === 200) {
                    message.success("更新楼栋号成功");
                    // onUpdateBuilding(building.uuid, { ...building, buildingNumber: updatedBuildingNumber }); // 更新父组件状态
                    const updatedItem = { ...item, buildingNumber: updatedBuildingNumber };
                    setItem(updatedItem);
                    onUpdateBuilding(item.uuid, updatedItem); // 回调传递更新信息
                }
            }).catch(err => {
                message.error("更新楼栋号失败");
            }).finally(() => {
                setModalOpen(false);
                form.resetFields();
            });
        });
    };

    // 删除楼栋
    const onDeleteBuilding = () => {
        if (!item) return;
        TnModal().deleteModal({
            onOk() {
                Modal.destroyAll();
                deleteBuilding(item.uuid).then(res => {
                    TnMessage.success("删除成功");
                    props.onDeleteBuilding();
                }).catch(() => {
                    TnMessage.error("删除失败")
                })
            },
            onCancel() {
                Modal.destroyAll();
            },
            content: "点击确认，删除所选中楼栋，并删除楼栋的所有模型",
            closable: true
        })
    }

    if (!item) {
        return (<div></div>)
    }
    return (
        <div className="building-item">
            <div className="building-title" onClick={handleExpandItem} onContextMenu={(e) => { e.preventDefault(); onRightClickBuilding(e) }}>
                {editing && <TnInput
                    className="small-input"
                    defaultValue={item.name}
                    onBlur={(value) => {
                        onUpdateBuilding(item.uuid, { name: value })
                        setEditing(false);
                    }}
                    onEscapeKeyDown={() => {
                        setEditing(false);
                    }}
                    onPressEnter={(value) => {
                        onUpdateBuilding(item.uuid, { name: value })
                        setEditing(false);
                    }}
                />}
                {!editing && (
                    <>
                        <div className="name">
                            <ShowSvg className={classNames({
                                "arrow-icon": true,
                                "expanded": item.expanded
                            })} />
                            {item.name}
                        </div>
                        <div className="add-model-btn" onClick={(e) => {
                            e.stopPropagation();
                            onClickAddModel();
                        }}><AddSvg />模型</div>
                    </>
                )}
            </div>
            {item.expanded && (
                <div className="model-list">
                    {(item.modelList || []).map((model, idx) => (
                        <ModelItem
                            key={idx}
                            model={model}
                            onUpdateModel={data => {
                                let newModel = { ...model, ...data };
                                item.modelList.splice(idx, 1, newModel);
                                setItem({ ...item });
                            }}
                            onDeleteModel={() => {
                                item.modelList.splice(idx, 1);
                                setItem({ ...item });
                            }}
                            // togglePreview={modelUuid => {
                            //     props.togglePreview(modelUuid)
                            // }}
                            // isShow={props.previewModelUuids.includes(model.uuid)}
                        />
                    ))}
                    {newModel && (
                        <div className="model-item">
                            <div className={classNames({
                                "model-title": true,
                                "hover": true,
                            })}>
                                <Select size='small' className="select-box" onChange={(value, option) => {
                                    let modelType = option['option']
                                    setNewModel({ ...newModel, type: modelType.value, name: modelType.label })
                                }}>
                                    {MODEL_TYPE_LIST.filter(item => item.value != ModelType.SITE).map(modelType => (
                                        <Select.Option value={modelType.value} option={modelType}>{modelType.label}</Select.Option>
                                    ))}
                                </Select>
                                <ConfirmSvg className="confirm-icon" onClick={onAddModel} />
                            </div>
                        </div>
                    )}
                </div>
            )}
            <ContextMenuFC
                visible={cmOpen}
                top={top}
                left={left}
                width={96}
                wrapperClassname='tn-right-context-menu'
                onCancel={() => { setCmOpen(false) }}
            >
                <div className="menu-item" onClick={() => { setEditing(true); setCmOpen(false) }}>重命名</div>
                <div className="menu-item" onClick={() => { onDeleteBuilding(); setCmOpen(false) }}>删除</div>
                <div className="menu-item" onClick={() => { onClickLink(); setCmOpen(false) }}>绑定楼栋号</div>
            </ContextMenuFC>
            <ConfirmModal
                title={"绑定楼栋号"}
                visible={modalOpen}
                setVisible={setModalOpen}
                onSaveClick={onConfirmSave}
                wrapClassName="create-project-modal"
                saveDisable={loading}
            >
                <div className="create-project-body">
                    <Form
                        colon={false}
                        form={form}
                        autoComplete="off"
                        labelCol={{ span: 5 }}
                    >
                        <Form.Item label="楼栋号:" name="buildingNumber" rules={[{ required: true, message: '楼栋号不能为空' }]}>
                            <Input type="number" autoFocus />
                        </Form.Item>
                        <Button loading={loading} htmlType="submit" style={{ display: 'none' }}></Button>
                    </Form>
                </div>
            </ConfirmModal>
        </div>
    )
}